import { pepApiRequest, RequestMethod } from './index';

/**
 * Feature flags response type
 */
export interface FeatureFlags {
  [key: string]: boolean;
}

/**
 * ======================================================
 * MANUAL TOGGLE: Change these values for local testing.
 * These are the single source of truth for defaults/fallbacks.
 * ======================================================
 */
const OMNIVERSE_STREAMING_DEFAULT = false;
const SPECTRO_DEFAULT = false  ;
const USE_CONSUMER_DEFAULT = false;

/**
 * Default feature flags - used when API fails or features are not returned
 */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  'pep.eiotp.kubernetes.useSpectro': SPECTRO_DEFAULT,
  'pep.eiotp.kubernetes.useOmniverseStreaming': OMNIVERSE_STREAMING_DEFAULT,
  'pep.eiotp.useConsumer': USE_CONSUMER_DEFAULT,
};

const FEATURE_FLAGS_TIMEOUT_MS = 8000;

interface FeatureFlagItem {
  name: string;
  enabled: boolean;
  description: string | null;
  label: string | null;
  lastModified: string | null;
  conditions: any | null;
}

interface FeatureFlagsResponse {
  totalCount: number;
  features: FeatureFlagItem[];
  retrievedAtUtc: string;
}

/**
 * Fetches feature flags from the backend API
 * @returns Promise containing feature flags object
 */
export const fetchFeatureFlags = async (): Promise<FeatureFlags> => {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(
      () => reject(new Error('Feature flags fetch timed out')),
      FEATURE_FLAGS_TIMEOUT_MS
    );
  });

  try {
    const response: FeatureFlagsResponse = await Promise.race([
      pepApiRequest({
        method: RequestMethod.Get,
        url: '/features',
        useProxy: false,
        skipErrorAlert: true,
        skipErrorCodes: [400, 401, 403, 404, 500],
      }),
      timeoutPromise,
    ]);

    // Parse the new response format: { features: [{ name, enabled, ... }] }
    if (response && Array.isArray(response.features)) {
      const parsed: FeatureFlags = {};
      response.features.forEach((feature) => {
        parsed[feature.name] = feature.enabled;
      });
      return {
        ...DEFAULT_FEATURE_FLAGS,
        ...parsed,
      };
    }

    // Fallback to defaults if response is invalid
    return DEFAULT_FEATURE_FLAGS;
  } catch (error) {
    console.error('Error fetching feature flags:', error);
    // Return defaults on any error
    return DEFAULT_FEATURE_FLAGS;
  }
};

/**
 * Get a specific feature flag value
 * @param flags - The feature flags object
 * @param flagName - The name of the flag to retrieve
 * @param defaultValue - Default value if flag doesn't exist
 * @returns The flag value or default
 */
export const getFeatureFlag = (
  flags: FeatureFlags | null,
  flagName: string,
  defaultValue = false
): boolean => {
  if (!flags || !(flagName in flags)) {
    return defaultValue;
  }
  const value = flags[flagName];
  return typeof value === 'boolean' ? value : defaultValue;
};

/**
 * Specific getter for Spectro feature flag
 */
export const isSpectroFeatureEnabled = (flags: FeatureFlags | null): boolean => {
  return getFeatureFlag(flags, 'pep.eiotp.kubernetes.useSpectro', SPECTRO_DEFAULT);
};

/**
 * Specific getter for Omniverse Streaming feature flag
 */
export const isOmniverseStreamingEnabled = (flags: FeatureFlags | null): boolean => {
  return getFeatureFlag(flags, 'pep.eiotp.kubernetes.useOmniverseStreaming', OMNIVERSE_STREAMING_DEFAULT);
};

/**
 * Specific getter for Consumer feature flag
 */
export const isConsumerFeatureEnabled = (flags: FeatureFlags | null): boolean => {
  return getFeatureFlag(flags, 'pep.eiotp.useConsumer', USE_CONSUMER_DEFAULT);
};
